import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Filter, Archive, ListPlus, X } from 'lucide-react';
import { useActionBarConfig } from '../../contexts/ActionBarContext';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import RecordNavigator from '@/components/ui/record-navigator';
import SearchBuilder, { applyFilters, matchesFreeText } from '@/components/search/SearchBuilder';
import UrlFilterChip from '@/components/UrlFilterChip';
import BulkAppKennungen from '@/components/app/BulkAppKennungen';
import useListKeyboardNav from '@/hooks/useListKeyboardNav';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useTableColumns from '@/hooks/useTableColumns';
import useTableSort from '@/hooks/useTableSort';
import SortableTh from '@/components/table/SortableTh';
import { footerToParams, getFooterSettings, setFooterSettings } from '@/components/FooterDateBar';

// Nach welchem Wert eine Spalte sortiert. Nachname zuerst, weil Listen in der
// Musikschule nach Nachnamen geführt werden.
const STUDENT_SORT_ACCESSORS = {
  name: (s) => `${s.last_name || ''} ${s.first_name || ''}`.trim(),
  email: (s) => s.email,
  phone: (s) => s.phone,
  city: (s) => s.city,
  birth_date: (s) => s.birth_date,
  enrollment_count: (s) => Number(s.enrollment_count || 0),
  status: (s) => s.status,
  created_at: (s) => s.created_at,
};

// Define filterable fields
const FILTER_FIELDS = [
  { key: 'first_name', label: 'Vorname', type: 'text' },
  { key: 'last_name', label: 'Nachname', type: 'text' },
  { key: 'email', label: 'E-Mail', type: 'text' },
  { key: 'phone', label: 'Telefon', type: 'text' },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'paused', label: 'Pausiert' },
  ]},
  { key: 'city', label: 'Ort', type: 'text' },
  { key: 'postal_code', label: 'PLZ', type: 'text' },
  { key: 'birth_date', label: 'Geburtsdatum', type: 'date' },
  { key: 'enrollment_count', label: 'Belegungen', type: 'number' },
];

const DEFAULT_COLUMNS = [
  { key: 'name', label: 'Name', visible: true },
  { key: 'email', label: 'E-Mail', visible: true },
  { key: 'phone', label: 'Telefon', visible: true },
  { key: 'city', label: 'Ort', visible: false },
  { key: 'birth_date', label: 'Geburtsdatum', visible: false },
  { key: 'enrollment_count', label: 'Belegungen', visible: false },
  { key: 'status', label: 'Status', visible: true },
  { key: 'created_at', label: 'Erstellt am', visible: true },
];

/** Stammdaten, die die Freitextsuche sieht. Erstelldatum und Status zählen nicht. */
export function studentSearchRecord(student) {
  const birth = student.date_of_birth || student.birth_date;
  return {
    name: `${student.first_name || ''} ${student.last_name || ''} ${student.last_name || ''}, ${student.first_name || ''}`,
    email: student.email,
    phone: student.phone,
    address: student.address,
    postal_code: student.postal_code,
    city: student.city,
    gkz: student.gkz,
    notes: student.notes,
    short_note: student.short_note,
    attention_source: student.attention_source,
    birth: [birth, formatDate(birth)].filter(Boolean).join(' '),
    person_nr: student.msv_person_nr,
    kassenzeichen: student.imikel_kassenzeichen,
    teachers: student.teachers || [],
    courses: student.courses || [],
    payers: (student.payers || []).map((payer) => [
      payer.name, payer.email, payer.phone, payer.address, payer.postal_code, payer.city,
    ].filter(Boolean).join(' ')),
  };
}

export default function StudentsList() {
  const { user } = useAuth();
  const { isSuperAdmin } = useTenant();
  const roleNames = new Set((user?.roles || []).map((role) => role.name));
  const isTeacher = (roleNames.has('teacher') || roleNames.has('lehrer'))
    && !isSuperAdmin
    && !['admin', 'secretary', 'sekretariat', 'verwaltung', 'management']
      .some((role) => roleNames.has(role));
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const [cfDefs, setCfDefs] = useState([]);
  const [cfFieldId, setCfFieldId] = useState('');
  const [cfValue, setCfValue] = useState('');
  const [cfMatchIds, setCfMatchIds] = useState(null); // null = Filter inaktiv

  const cfSelected = useMemo(() => cfDefs.find(d => d.id === cfFieldId) || null, [cfDefs, cfFieldId]);

  useEffect(() => {
    api.get('/custom-fields?entity_type=student')
      .then(r => setCfDefs(r.data || []))
      .catch(() => setCfDefs([]));
  }, []);

  useEffect(() => {
    if (!cfFieldId) { setCfMatchIds(null); return; }
    const t = setTimeout(async () => {
      try {
        const v = cfSelected?.field_type === 'checkbox' ? 'true' : cfValue;
        const r = await api.get(`/custom-fields/filter/student?definition_id=${cfFieldId}${v ? `&value=${encodeURIComponent(v)}` : ''}`);
        setCfMatchIds(new Set(r.data.entity_ids || []));
      } catch { setCfMatchIds(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [cfFieldId, cfValue, cfSelected]);

  const resetCfFilter = () => { setCfFieldId(''); setCfValue(''); setCfMatchIds(null); };
  const { columns, setColumns } = useTableColumns('students-list', DEFAULT_COLUMNS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [onlyActive, setOnlyActive] = useState(true);
  /** null = default; false = aktive Stammdaten ohne aktive Belegung */
  const [hasEnrollmentFilter, setHasEnrollmentFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveMonths, setArchiveMonths] = useState(0);
  const [archivableCount, setArchivableCount] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const navigate = useNavigate();
  // Honor deep links from dashboard / Rechnungslauf-Vorschau
  const [searchParams] = useSearchParams();
  const [footerTick, setFooterTick] = useState(0);
  useEffect(() => {
    const onFooter = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', onFooter);
    return () => window.removeEventListener('footer-settings-changed', onFooter);
  }, []);
  useEffect(() => {
    const flag = searchParams.get('is_active');
    if (flag === 'false') setOnlyActive(false);
    else if (flag === 'true') setOnlyActive(true);

    const he = searchParams.get('has_enrollment');
    if (he === 'false') {
      setHasEnrollmentFilter(false);
      setOnlyActive(false);
    } else if (he === 'true') {
      setHasEnrollmentFilter(true);
      setOnlyActive(true);
    }

    const st = searchParams.get('status');
    if (st === 'active' || st === 'inactive' || st === 'paused') {
      setStatusFilter(st);
    }

    const gid = searchParams.get('gkz_group_id');
    const gkz = searchParams.get('gkz');
    if (gid || gkz) {
      const cur = getFooterSettings();
      const nextGroup = gid || '';
      const nextGkz = gid ? '' : (gkz || '');
      if (cur.gkzGroupId !== nextGroup || cur.gkz !== nextGkz) {
        setFooterSettings({ gkzGroupId: nextGroup, gkz: nextGkz });
      }
    }
  }, [searchParams]);

  const loadSeq = useRef(0);
  const loadStudents = useCallback(async () => {
    const seq = ++loadSeq.current;
    const term = String(searchTerm || '').trim();
    // Nur der erste Aufruf ersetzt die Tabelle durch den Ladekreis.
    // Eine laufende Suche filtert die schon geladenen Zeilen sofort.
    if (seq === 1) setLoading(true);
    try {
      // Load in batches since API has limit of 100
      let allData = [];
      let offset = 0;
      const batchSize = 100;

      while (true) {
        const params = { limit: batchSize, offset };
        if (term) params.search = term;
        if (statusFilter) {
          params.status = statusFilter;
        }
        if (hasEnrollmentFilter === false) {
          params.has_enrollment = false;
          if (!statusFilter) params.status = 'active';
        } else if (hasEnrollmentFilter === true || onlyActive) {
          params.has_enrollment = true;
        }
        const footer = getFooterSettings();
        const gkzGroupId = searchParams.get('gkz_group_id') || footer.gkzGroupId;
        const gkz = searchParams.get('gkz') || footer.gkz;
        if (gkzGroupId) params.gkz_group_id = gkzGroupId;
        if (gkz) params.gkz = gkz;
        const period = footerToParams(footer);
        if (period.period_start) params.period_start = period.period_start;
        if (period.period_end) params.period_end = period.period_end;
        if (period.active_on) params.active_on = period.active_on;
        if (period.active_only) params.active_only = true;
        const response = await api.get('/students', { params });
        if (seq !== loadSeq.current) return;
        const batch = response.data;
        if (!batch || batch.length === 0) break;
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        offset += batchSize;
      }

      if (seq !== loadSeq.current) return;
      setAllStudents(allData);
      setCurrentIndex(0);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      console.error('Error loading students:', error);
      toast.error('Fehler beim Laden der Schüler');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [onlyActive, hasEnrollmentFilter, statusFilter, searchParams, footerTick, searchTerm]);

  useEffect(() => {
    const term = String(searchTerm || '').trim();
    let handle;
    if (!term) loadStudents();
    else handle = setTimeout(() => { loadStudents(); }, 250);
    return () => {
      if (handle) clearTimeout(handle);
      // Laufende Ladung verwerfen, sonst überschreibt die volle Liste die Treffer.
      loadSeq.current += 1;
    };
  }, [loadStudents, searchTerm]);

  // Live preview of archivable count whenever the dialog is open or months change
  useEffect(() => {
    if (!archiveOpen) return;
    let cancelled = false;
    setArchivableCount(null);
    api.get('/students/stats/archivable', { params: { months: archiveMonths } })
      .then((res) => { if (!cancelled) setArchivableCount(res.data.archivable_count); })
      .catch(() => { if (!cancelled) setArchivableCount(null); });
    return () => { cancelled = true; };
  }, [archiveOpen, archiveMonths]);

  const handleBulkArchive = async () => {
    setArchiveLoading(true);
    try {
      const res = await api.post('/students/bulk-archive', null, { params: { months: archiveMonths } });
      toast.success(`${res.data.archived_count} Schüler archiviert`);
      setArchiveOpen(false);
      loadStudents();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Archivieren');
    } finally {
      setArchiveLoading(false);
    }
  };

  const { sort, toggleSort, sortRows } = useTableSort(STUDENT_SORT_ACCESSORS, { key: 'name' });

  // Apply filters client-side for instant feedback
  const filteredStudents = useMemo(() => {
    let pool = allStudents;
    if (String(searchTerm || '').trim()) {
      pool = pool.filter((student) => matchesFreeText(studentSearchRecord(student), searchTerm));
    }
    let result = applyFilters(pool, filters, '');
    if (cfMatchIds) {
      result = result.filter(s => cfMatchIds.has(s.id));
    }
    return sortRows(result);
  }, [allStudents, filters, searchTerm, cfMatchIds, sortRows]);

  const { getRowProps } = useListKeyboardNav({
    items: filteredStudents,
    onSelect: (s) => navigate(`/students/${s.id}`),
  });

  useActionBarConfig({
    list: { kind: 'student', ids: filteredStudents.map((s) => s.id) },
    onNew: () => navigate('/students/new'),
    newLabel: 'Neuen Schüler anlegen',
  }, [filteredStudents]);

  const handleNavigate = (newIndex) => {
    setCurrentIndex(newIndex);
    const row = document.querySelector(`[data-testid="student-row-${filteredStudents[newIndex]?.id}"]`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('bg-blue-50');
      setTimeout(() => row.classList.remove('bg-blue-50'), 2000);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      inactive: { label: 'Inaktiv', className: 'bg-slate-100 text-slate-700' },
      paused: { label: 'Pausiert', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const config = variants[status] || variants.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const isColumnVisible = (key) => {
    const col = columns.find(c => c.key === key);
    return col ? col.visible : true;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1" data-testid="students-title">
              Schüler
            </h1>
            <p className="text-slate-600">
              {filteredStudents.length} von {allStudents.length} Schülern
              {hasEnrollmentFilter === false && (
                <span className="ml-2 text-xs text-amber-700">(aktiv, ohne aktive Belegung)</span>
              )}
              {hasEnrollmentFilter !== false && onlyActive && (
                <span className="ml-2 text-xs text-emerald-700">(nur aktive Belegungen)</span>
              )}
            </p>
            <UrlFilterChip
              params={[
                {
                  key: 'is_active',
                  label: 'Aktiv-Filter',
                  valueLabels: { true: 'Nur aktive', false: 'Alle Schüler' },
                },
                {
                  key: 'has_enrollment',
                  label: 'Belegung',
                  valueLabels: { true: 'Mit Belegung', false: 'Ohne aktive Belegung' },
                },
                {
                  key: 'status',
                  label: 'Status',
                  valueLabels: { active: 'Aktiv', inactive: 'Inaktiv', paused: 'Pausiert' },
                },
                { key: 'gkz_group_id', label: 'GKZ-Gruppe' },
                { key: 'gkz', label: 'GKZ' },
              ]}
              onReset={() => {
                setOnlyActive(true);
                setHasEnrollmentFilter(null);
                setStatusFilter(null);
                setFooterSettings({ gkzGroupId: '', gkz: '' });
              }}
            />
          </div>
          {!isTeacher && <div className="flex items-center gap-2">
            <BulkAppKennungen personType="student" audienceLabel="Schüler" />
            <Button
              variant="outline"
              onClick={() => { setArchiveOpen(true); setArchiveMonths(0); }}
              data-testid="open-bulk-archive-dialog"
              className="border-amber-300 text-amber-800 hover:bg-brand-red/5"
            >
              <Archive className="w-4 h-4 mr-2" />
              Altdaten archivieren
            </Button>
            <Button
              variant={hasEnrollmentFilter === false ? 'default' : 'outline'}
              onClick={() => {
                if (hasEnrollmentFilter === false) {
                  setHasEnrollmentFilter(null);
                  setStatusFilter(null);
                  setOnlyActive(true);
                  navigate('/students', { replace: true });
                } else {
                  setHasEnrollmentFilter(false);
                  setStatusFilter('active');
                  setOnlyActive(false);
                  navigate('/students?status=active&has_enrollment=false', { replace: true });
                }
              }}
              data-testid="toggle-without-enrollment"
              className={hasEnrollmentFilter === false ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Ohne Belegung
            </Button>
            <Button
              variant={onlyActive && hasEnrollmentFilter !== false ? 'default' : 'outline'}
              onClick={() => {
                setHasEnrollmentFilter(null);
                setStatusFilter(null);
                setOnlyActive((v) => !v);
                navigate('/students', { replace: true });
              }}
              data-testid="toggle-only-active-students"
              className={onlyActive && hasEnrollmentFilter !== false ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              {onlyActive && hasEnrollmentFilter !== false ? 'Nur Aktive' : 'Alle Schüler'}
            </Button>
            <Button
              onClick={() => navigate('/students/new')}
              data-testid="add-student-button"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuer Schüler
            </Button>
          </div>}
        </div>

        {/* SearchBuilder */}
        <SearchBuilder
          fields={FILTER_FIELDS}
          onFilterChange={setFilters}
          onSearch={(value) => { setSearchTerm(value); setCurrentIndex(0); }}
          columns={columns}
          onColumnsChange={setColumns}
          placeholder="Alles durchsuchen…"
          className="mb-4"
        />

        {/* Zusatzfeld-Filter */}
        {cfDefs.length > 0 && (
          <div className="mb-4 flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="custom-field-filter">
            <span className="flex items-center gap-1.5 text-sm text-slate-600">
              <ListPlus className="w-4 h-4 text-sky-600" />
              Zusatzfeld:
            </span>
            <Select value={cfFieldId || undefined} onValueChange={(v) => { setCfFieldId(v); setCfValue(''); }}>
              <SelectTrigger className="w-56 h-9" data-testid="cf-filter-field-select">
                <SelectValue placeholder="Feld wählen..." />
              </SelectTrigger>
              <SelectContent>
                {cfDefs.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cfSelected && cfSelected.field_type === 'select' && (
              <Select value={cfValue || undefined} onValueChange={setCfValue}>
                <SelectTrigger className="w-48 h-9" data-testid="cf-filter-value-select">
                  <SelectValue placeholder="Beliebiger Wert" />
                </SelectTrigger>
                <SelectContent>
                  {(cfSelected.options || []).map(o => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {cfSelected && ['text', 'number', 'date'].includes(cfSelected.field_type) && (
              <Input
                value={cfValue}
                onChange={(e) => setCfValue(e.target.value)}
                placeholder={cfSelected.field_type === 'date' ? 'JJJJ-MM-TT' : 'Wert (leer = beliebig)'}
                className="w-48 h-9"
                data-testid="cf-filter-value-input"
              />
            )}
            {cfSelected && cfSelected.field_type === 'checkbox' && (
              <span className="text-xs text-slate-500">= Ja</span>
            )}
            {cfFieldId && (
              <>
                <Badge variant="outline" className="text-sky-700 border-sky-300" data-testid="cf-filter-count">
                  {cfMatchIds ? `${filteredStudents.length} Treffer` : '…'}
                </Badge>
                <Button variant="ghost" size="sm" onClick={resetCfFilter} data-testid="cf-filter-reset">
                  <X className="w-4 h-4 mr-1" /> Zurücksetzen
                </Button>
              </>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-600" data-testid="students-result-count">
              {filteredStudents.length} Ergebnisse
            </span>
            
            {filteredStudents.length > 0 && (
              <RecordNavigator
                currentIndex={currentIndex}
                totalCount={filteredStudents.length}
                onNavigate={handleNavigate}
                records={filteredStudents}
                getRecordLabel={(student) => `${student.last_name}, ${student.first_name}`}
              />
            )}
          </div>

          <div className="overflow-x-auto" style={{ overflowAnchor: 'none' }}>
            <table
              className="w-full"
              data-testid="students-table"
              key={filteredStudents.map((student) => student.id).join('|') || 'empty'}
            >
              <thead className="bg-slate-50 text-slate-500 font-medium text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  {DEFAULT_COLUMNS.filter((col) => isColumnVisible(col.key)).map((col) => (
                    <SortableTh
                      key={col.key}
                      sortKey={col.key}
                      sort={sort}
                      onSort={toggleSort}
                      testIdPrefix="students-sort"
                    >
                      {col.label}
                    </SortableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      Keine Schüler gefunden
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student, idx) => (
                    <tr
                      key={student.id}
                      data-testid={`student-row-${student.id}`}
                      className="border-b border-slate-100 hover:bg-brand-red/5 transition-colors cursor-pointer data-[keyboard-selected=true]:bg-brand-red/10 data-[keyboard-selected=true]:ring-2 data-[keyboard-selected=true]:ring-brand-red/30 data-[keyboard-selected=true]:ring-inset"
                      {...getRowProps(idx, student)}
                    >
                      {isColumnVisible('name') && (
                        <td className="p-4 text-sm text-slate-700 font-medium">
                          {student.first_name} {student.last_name}
                        </td>
                      )}
                      {isColumnVisible('email') && (
                        <td className="p-4 text-sm text-slate-600">{student.email || '-'}</td>
                      )}
                      {isColumnVisible('phone') && (
                        <td className="p-4 text-sm text-slate-600">{student.phone || '-'}</td>
                      )}
                      {isColumnVisible('city') && (
                        <td className="p-4 text-sm text-slate-600">{student.city || '-'}</td>
                      )}
                      {isColumnVisible('birth_date') && (
                        <td className="p-4 text-sm text-slate-600">{formatDate(student.birth_date) || '-'}</td>
                      )}
                      {isColumnVisible('enrollment_count') && (
                        <td className="p-4 text-sm text-slate-600">{student.enrollment_count || 0}</td>
                      )}
                      {isColumnVisible('status') && (
                        <td className="p-4 text-sm">{getStatusBadge(student.status)}</td>
                      )}
                      {isColumnVisible('created_at') && (
                        <td className="p-4 text-sm text-slate-600">{formatDate(student.created_at)}</td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent data-testid="bulk-archive-dialog">
          <DialogHeader>
            <DialogTitle>Altdaten archivieren</DialogTitle>
            <DialogDescription>
              Setzt alle Schüler mit <strong>Status „Aktiv"</strong> auf <strong>„Inaktiv"</strong>,
              die keine laufende Belegung haben. Die Daten bleiben erhalten — nur der Status ändert sich.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Mindestens X Monate ohne Belegung beendet (optional)
              </label>
              <Input
                type="number"
                min={0}
                max={120}
                value={archiveMonths}
                onChange={(e) => setArchiveMonths(Math.max(0, parseInt(e.target.value || '0', 10)))}
                data-testid="bulk-archive-months-input"
                className="w-32"
              />
              <p className="text-xs text-slate-500 mt-1">
                0 = sofort alle Schüler ohne aktive Belegung archivieren. Höhere Werte schonen kürzlich beendete Belegungen.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-sm text-amber-900">
                Vorschau:{' '}
                <span className="font-bold" data-testid="bulk-archive-preview-count">
                  {archivableCount === null ? '...' : archivableCount}
                </span>{' '}
                Schüler werden archiviert.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveOpen(false)}
              data-testid="bulk-archive-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleBulkArchive}
              disabled={archiveLoading || !archivableCount}
              data-testid="bulk-archive-confirm"
              className="bg-brand-red hover:bg-brand-red/90 text-white"
            >
              <Archive className="w-4 h-4 mr-2" />
              {archiveLoading ? 'Archiviere...' : `${archivableCount ?? '...'} Schüler archivieren`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
